import type { Emitter } from "@core/events/emitter";
import type { Vec3 } from "@models/data/entityData";
import type { KnownUnits } from "@models/utils/scaleUnits";

/**
 * `IMeasurementsModel` — web port of Unity's `IMeasurementsLayerModel`
 * (`Models/AnnotationsModel/MeasurementsLayerModel.cs`), restricted to the
 * Point-to-Point ruler. This is the second of the Measure tool's two
 * `MeasurementToolMode`s; Dimensions (bounding box) already ships as
 * `IBoundingBoxToolModel`.
 *
 * **Session-local, first slice.** Unity persists measurements as Dynamo rows
 * (`ProjectDataModel.CreateMeasurementAsync` / `UpdateMeasurementAsync`) and
 * they carry the full annotation payload — creator, resolve state, replies,
 * notifications. None of that is here yet: measurements live for the session
 * and are dropped on scene/project change, the same staging the annotations
 * subsystem went through (text-only → cloud-backed → 3D markers → live
 * events). The persisted shape is `MeasurementData.cs`, and the wire events
 * are tc 49 (`MeasurementEventData`) / tc 50 (`DeleteMeasurementEventData`).
 *
 * **Endpoints are stored ENTITY-LOCAL, not world.** Unity anchors each end to
 * the hit part's transform and stores a local offset, so moving or exploding a
 * model carries its measurements with it. The model layer cannot import Three,
 * so the world↔local conversion goes through {@link IMeasurementAnchorHost},
 * which the viewport registers — the same seam pattern as
 * `IPreviewCaptureHost` and `reportSceneBuild*`.
 */

/** Which handle of a measurement an interaction is addressing. */
export type MeasurementPointType = "start" | "end" | "pill";

/**
 * Unity `MeasurementToolMode` (`Tools/MeasurementTool.cs`). One Measure tool,
 * two behaviours: `boundingBox` shows an entity's dimensions on hover/click,
 * `pointToPoint` drops a two-ended ruler. Unity defaults the field to
 * `PointToPoint` but the ViewModel's `IsBoundingBoxMode` toggle decides what
 * the user actually starts in.
 */
export type MeasurementToolMode = "boundingBox" | "pointToPoint";

export interface Measurement {
  readonly id: string;
  /** Snapshot the START end is anchored to (`MeasurementData.StartEntityId`). */
  readonly startSnapshotId: string;
  readonly endSnapshotId: string;
  /** Entity-local offsets (`StartPosition` / `EndPosition`). */
  readonly startLocal: Vec3;
  readonly endLocal: Vec3;
  /**
   * Perpendicular offset of the ruler line from the straight start→end
   * segment (`RulerOffset`), so overlapping measurements can be fanned apart.
   */
  readonly rulerOffset: Vec3;
  /** True while a handle is being dragged — Unity `IsEdited`. */
  readonly isEdited: boolean;
  /**
   * True when the dragged handle is currently over empty space. Unity uses it
   * to decide, on release, whether to delete a new measurement or revert an
   * edited one rather than leaving an end floating in the void.
   */
  readonly isOverEmptyArea: boolean;
}

/**
 * Renderer-side seam: resolves a snapshot id to its live world transform so
 * the model can convert between world and entity-local space. Registered by
 * `ViewportView`; when absent (headless / no viewport) the measurement layer
 * degrades to world-space storage rather than throwing.
 */
export interface IMeasurementAnchorHost {
  /** `null` when the snapshot has no rendered node right now. */
  worldToLocal(snapshotId: string, world: Vec3): Vec3 | null;
  localToWorld(snapshotId: string, local: Vec3): Vec3 | null;
}

export interface IMeasurementsModel {
  /** Any add / update / remove. Carries the full current list. */
  readonly onChanged: Emitter<ReadonlyArray<Measurement>>;
  /** Display unit changed — the bounding-box pills listen too. */
  readonly onUnitChanged: Emitter<KnownUnits>;

  /** Mode changed — both tool controllers gate their input on this. */
  readonly onModeChanged: Emitter<MeasurementToolMode>;

  readonly measurements: ReadonlyArray<Measurement>;
  /**
   * Unity `IMeasurementsLayerModel.Unit`; drives every distance readout —
   * the ruler pills AND the bounding-box dimension labels, which is why the
   * one dropdown lives on this model rather than on either controller.
   */
  unit: KnownUnits;
  /** Unity `MeasurementTool.Mode` / `MeasurementToolViewModel.IsBoundingBoxMode`. */
  mode: MeasurementToolMode;

  attachAnchorHost(host: IMeasurementAnchorHost | null): void;

  /**
   * Port of `BeginNewMeasurement`. Both ends start at the same point; the END
   * is then dragged. Returns the new id, or `""` when the target can't anchor
   * a measurement (Unity's `TryGetMeasurementAnchor` failure).
   */
  beginNewMeasurement(snapshotId: string, worldPos: Vec3): string;

  /**
   * Port of `UpdateMeasurement`. A `null` snapshotId means the pointer is over
   * empty space: the endpoint still follows the ray (so the rubber-band keeps
   * up) but the anchor is left on its last valid part and `isOverEmptyArea`
   * is set.
   */
  updateMeasurement(
    id: string,
    pointType: MeasurementPointType,
    snapshotId: string | null,
    worldPos: Vec3,
  ): void;

  /**
   * Port of `UpdateRulerOffset`. Unity projects the raw drag perpendicular to
   * the measurement line (two cross products) while preserving its magnitude,
   * so the ruler slides sideways instead of along itself.
   */
  updateRulerOffset(id: string, initialLocalOffset: Vec3, worldOffset: Vec3): void;

  /**
   * Port of `FinishNewMeasurement`. Discards the measurement when it ended
   * over empty space or is degenerate (world length < 1e-5) — that is Unity's
   * own guard against a click producing a zero-length ruler.
   */
  finishNewMeasurement(id: string): void;

  /**
   * Port of `FinishEditedMeasurement`. Reverts the drag when it ended over
   * empty space. For the pill, a residual offset below the snapback threshold
   * collapses back to zero.
   */
  finishEditedMeasurement(id: string, pointType: MeasurementPointType): void;

  deleteMeasurement(id: string): void;
  clear(): void;

  /** World-space length in metres, or `null` if an anchor can't resolve. */
  getWorldDistance(id: string): number | null;
  /** `"12.34 cm"` — Unity `GetDistanceWithUnits`. */
  getDistanceWithUnits(id: string): string;

  dispose(): void;
}

export const MEASUREMENTS_MODEL = Symbol.for("Campfire.MeasurementsModel");
